import React from "react";

import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { cn } from "@/utils";
import { useRole } from "@/utils/hooks/use-role";
import type { ShiftCard } from "@/utils/shift-card";
import { RoleIcon } from "./icons/role-icons";
import StarRating from "./star-rating";

export const IconCoffee: React.FC<{ muted?: boolean }> = ({
  muted = false,
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="54"
    height="52"
    viewBox="0 0 54 52"
    fill="none"
    aria-hidden
  >
    <path
      d="M27.8071 4.95935C27.8071 6.10832 26.8866 6.10831 26.8866 7.25992C26.8866 8.41153 27.8071 8.41152 27.8071 9.56049C27.8071 10.7095 26.8866 10.7121 26.8866 11.8664C26.8866 13.0206 27.8071 13.018 27.8071 14.1722M19.1983 1C19.1983 2.14897 18.2778 2.14899 18.2778 3.3006C18.2778 4.45221 19.1983 4.4522 19.1983 5.60117C19.1983 6.75014 18.2778 6.75278 18.2778 7.90703C18.2778 9.06129 19.1983 9.05864 19.1983 10.2129M41.3527 26.6973H45.1111C47.5876 26.6973 49.5947 28.7074 49.5947 31.1876C49.5947 33.6677 47.5876 35.6778 45.1111 35.6778H40.034M40.2979 22.4712H44.597C49.4762 22.4712 53.4324 26.4331 53.4324 31.3195C53.4324 36.206 49.4762 40.1679 44.597 40.1679H37.9242M19.1982 21.943V27.4897M43.128 29.1511C43.128 30.728 42.9434 31.8876 42.582 33.3535M37.2807 42.532C33.8494 45.8548 29.1759 47.8991 24.0276 47.8991C13.4963 47.8991 4.9563 39.3492 4.9563 28.7998V19.9276C4.9563 19.1432 5.59192 18.5066 6.37524 18.5066H41.6773C42.4606 18.5066 43.0962 19.1432 43.0962 19.9276M37.9241 47.0354H48.6083C48.6795 47.0354 48.7376 47.0935 48.7376 47.1648V47.6006C47.3265 49.7189 44.9054 51 42.3049 51H7.43534C4.83483 51 2.41103 49.7189 1 47.6006V47.1516C1 47.0882 1.05011 47.038 1.11341 47.038H10.4948M22.2605 38.8473H16.1364C15.7882 38.8473 15.506 38.5647 15.506 38.216V30.8653C15.506 30.543 15.6405 30.2366 15.8753 30.0174L18.0169 28.0206H20.38L22.5216 30.0174C22.7563 30.2366 22.8908 30.543 22.8908 30.8653V38.216C22.8908 38.5647 22.6086 38.8473 22.2605 38.8473Z"
      stroke={muted ? "#A0A0A0" : "url(#g1)"}
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
    <defs>
      <linearGradient
        id="g1"
        x1="1"
        y1="26"
        x2="50.6129"
        y2="23.033"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FBC468" />
        <stop offset="1" stopColor="#8F5600" />
      </linearGradient>
    </defs>
  </svg>
);
export const IconRunner: React.FC<{ muted?: boolean }> = ({
  muted = false,
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="48"
    height="52"
    viewBox="0 0 48 52"
    fill="none"
    aria-hidden
  >
    <path
      d="M23.3328 17.8797V26.2668M17.5348 27.9871V28.0022C17.5348 27.2818 18.1189 26.6968 18.8383 26.6968H27.8272C28.5466 26.6968 29.1307 27.2818 29.1307 28.0022V27.9871M27.473 4.31404C28.3019 2.63878 29.9038 1.38073 31.888 1.07105C35.1305 0.567825 38.1669 2.79147 38.6716 6.03878C39.174 9.28608 36.9537 12.3269 33.7111 12.8323M16.8907 10.998L23.5476 10.9744M21.4 50.9978V49.4258L24.6705 45.9591L35.3945 33.5806C33.9772 33.4344 32.5514 33.7656 31.3424 34.5247L23.9554 39.1613C23.8953 39.3591 19.0551 38.6795 18.9885 38.4838L16.4009 33.5398C16.2012 33.1118 15.7739 33.0667 15.3015 33.0667C14.7088 33.0667 14.2042 33.2688 14.1054 33.8538L13.8499 43.3505C13.8198 44.5656 14.2299 45.7505 15.0051 46.686L15.1576 46.8688L14.0989 50.0903V51M25.0463 37.9247L24.3097 33.1462H25.0699C25.9761 33.1462 26.79 33.701 27.1228 34.5462L28.0097 36.5656M19.0381 15.5421V14.0044C19.0381 12.3442 17.6939 10.998 16.0361 10.998H15.6324C14.548 10.998 13.6697 10.1184 13.6697 9.03241V6.76791C13.6697 6.72705 13.7019 6.69478 13.7427 6.69478H32.7105C32.7513 6.69478 32.7835 6.72705 32.7835 6.76791V9.41091C32.7835 10.2862 32.0749 10.9959 31.2009 10.9959H29.9876C28.3298 10.9959 26.9856 12.3421 26.9856 14.0023V15.5399C26.9856 16.8303 25.9398 17.8776 24.6514 17.8776H21.3745C20.086 17.8776 19.0403 16.8303 19.0403 15.5399L19.0381 15.5421ZM1.05368 29.0623H47.115C47.1446 29.0623 47.1686 29.0864 47.1686 29.1161V32.2344C47.1686 32.2641 47.1446 32.2881 47.115 32.2881H1.05368C1.02404 32.2881 1 32.2641 1 32.2344V29.1161C1 29.0864 1.02404 29.0623 1.05368 29.0623Z"
      stroke={muted ? "#A0A0A0" : "url(#g2)"}
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
    <defs>
      <linearGradient
        id="g2"
        x1="1"
        y1="26"
        x2="44.721"
        y2="23.6977"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FBC468" />
        <stop offset="1" stopColor="#8F5600" />
      </linearGradient>
    </defs>
  </svg>
);
export const IconHost: React.FC<{ muted?: boolean }> = ({ muted = false }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="46"
    height="52"
    viewBox="0 0 46 52"
    fill="none"
    aria-hidden
  >
    <path
      d="M32.2626 33.4766V39.5514M35.9952 39.785H28.7629M28.9964 26.9347H33.4292M16.8646 43.5234H1.06999C1.03033 43.5234 1 43.493 1 43.4533V1.0701C1 1.03039 1.03033 1 1.06999 1H22.4638M19.9465 8.5186L26.0031 1.23353C26.0684 1.15409 26.1967 1.20083 26.1967 1.30363V6.37372M7.29923 7.54195H16.1647M7.29923 17.3552H16.1647M7.29923 26.9347H16.398M7.29923 36.7477H16.398M32.5099 33.166C34.7683 33.166 36.5554 31.2571 36.4107 29.0001L35.8158 22.7267H29.204L28.6091 29.0001C28.4645 31.2571 30.2539 33.166 32.5099 33.166ZM20 8.4766H44.758C44.8147 8.4766 44.8606 8.52262 44.8606 8.57939V50.8972C44.8606 50.954 44.8147 51 44.758 51H20C19.9433 51 19.8973 50.954 19.8973 50.8972V8.57939C19.8973 8.52262 19.9433 8.4766 20 8.4766Z"
      stroke={muted ? "#A0A0A0" : "url(#g3)"}
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
    <defs>
      <linearGradient
        id="g3"
        x1="1"
        y1="26"
        x2="42.5466"
        y2="23.9216"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FBC468" />
        <stop offset="1" stopColor="#8F5600" />
      </linearGradient>
    </defs>
  </svg>
);
const CalIcon = ({ muted = false }: { muted?: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="14"
    viewBox="0 0 12 14"
    fill="none"
  >
    <path
      d="M3.42857 0C3.90268 0 4.28571 0.391016 4.28571 0.875V1.75H7.71429V0.875C7.71429 0.391016 8.09732 0 8.57143 0C9.04554 0 9.42857 0.391016 9.42857 0.875V1.75H10.7143C11.4241 1.75 12 2.33789 12 3.0625V4.375H0V3.0625C0 2.33789 0.575893 1.75 1.28571 1.75H2.57143V0.875C2.57143 0.391016 2.95446 0 3.42857 0ZM0 5.25H12V12.6875C12 13.4121 11.4241 14 10.7143 14H1.28571C0.575893 14 0 13.4121 0 12.6875V5.25ZM1.71429 7.4375V8.3125C1.71429 8.55313 1.90714 8.75 2.14286 8.75H3C3.23571 8.75 3.42857 8.55313 3.42857 8.3125V7.4375C3.42857 7.19688 3.23571 7 3 7H2.14286C1.90714 7 1.71429 7.19688 1.71429 7.4375ZM5.14286 7.4375V8.3125C5.14286 8.55313 5.33571 8.75 5.57143 8.75H6.42857C6.66429 8.75 6.85714 8.55313 6.85714 8.3125V7.4375C6.85714 7.19688 6.66429 7 6.42857 7H5.57143C5.33571 7 5.14286 7.19688 5.14286 7.4375ZM9 7C8.76429 7 8.57143 7.19688 8.57143 7.4375V8.3125C8.57143 8.55313 8.76429 8.75 9 8.75H9.85714C10.0929 8.75 10.2857 8.55313 10.2857 8.3125V7.4375C10.2857 7.19688 10.0929 7 9.85714 7H9ZM1.71429 10.9375V11.8125C1.71429 12.0531 1.90714 12.25 2.14286 12.25H3C3.23571 12.25 3.42857 12.0531 3.42857 11.8125V10.9375C3.42857 10.6969 3.23571 10.5 3 10.5H2.14286C1.90714 10.5 1.71429 10.6969 1.71429 10.9375ZM5.57143 10.5C5.33571 10.5 5.14286 10.6969 5.14286 10.9375V11.8125C5.14286 12.0531 5.33571 12.25 5.57143 12.25H6.42857C6.66429 12.25 6.85714 12.0531 6.85714 11.8125V10.9375C6.85714 10.6969 6.66429 10.5 6.42857 10.5H5.57143ZM8.57143 10.9375V11.8125C8.57143 12.0531 8.76429 12.25 9 12.25H9.85714C10.0929 12.25 10.2857 12.0531 10.2857 11.8125V10.9375C10.2857 10.6969 10.0929 10.5 9.85714 10.5H9C8.76429 10.5 8.57143 10.6969 8.57143 10.9375Z"
      fill={muted ? "#A0A0A0" : "url(#paint0_linear_688_6205)"}
    />
    <defs>
      <linearGradient
        id="paint0_linear_688_6205"
        x1="1.78814e-07"
        y1="14"
        x2="9.05477"
        y2="-1.69876"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#100505" />
        <stop offset="1" stopColor="#511A10" />
      </linearGradient>
    </defs>
  </svg>
);
const ClockIcon = ({ muted = false }: { muted?: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
  >
    <path
      d="M7 14C8.85652 14 10.637 13.2625 11.9497 11.9497C13.2625 10.637 14 8.85652 14 7C14 5.14348 13.2625 3.36301 11.9497 2.05025C10.637 0.737498 8.85652 0 7 0C5.14348 0 3.36301 0.737498 2.05025 2.05025C0.737498 3.36301 0 5.14348 0 7C0 8.85652 0.737498 10.637 2.05025 11.9497C3.36301 13.2625 5.14348 14 7 14ZM7.65625 3.28125V7C7.65625 7.21875 7.54687 7.42383 7.36367 7.54688L4.73867 9.29688C4.43789 9.49922 4.03047 9.41719 3.82812 9.11367C3.62578 8.81016 3.70781 8.40547 4.01133 8.20312L6.34375 6.65V3.28125C6.34375 2.91758 6.63633 2.625 7 2.625C7.36367 2.625 7.65625 2.91758 7.65625 3.28125Z"
      fill={muted ? "#A0A0A0" : "url(#paint0_linear_688_6208)"}
    />
    <defs>
      <linearGradient
        id="paint0_linear_688_6208"
        x1="2.08616e-07"
        y1="14"
        x2="8.3117"
        y2="-2.81221"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#100505" />
        <stop offset="1" stopColor="#511A10" />
      </linearGradient>
    </defs>
  </svg>
);
const PinIcon = ({ muted = false }: { muted?: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="10"
    height="14"
    viewBox="0 0 10 14"
    fill="none"
  >
    <path
      d="M5.61719 13.6854C6.95312 11.9254 10 7.65967 10 5.26362C10 2.35766 7.76042 0 5 0C2.23958 0 0 2.35766 0 5.26362C0 7.65967 3.04688 11.9254 4.38281 13.6854C4.70312 14.1049 5.29688 14.1049 5.61719 13.6854ZM5 3.50908C5.44203 3.50908 5.86595 3.69393 6.17851 4.02297C6.49107 4.35201 6.66667 4.79829 6.66667 5.26362C6.66667 5.72895 6.49107 6.17523 6.17851 6.50427C5.86595 6.83331 5.44203 7.01816 5 7.01816C4.55797 7.01816 4.13405 6.83331 3.82149 6.50427C3.50893 6.17523 3.33333 5.72895 3.33333 5.26362C3.33333 4.79829 3.50893 4.35201 3.82149 4.02297C4.13405 3.69393 4.55797 3.50908 5 3.50908Z"
      fill={muted ? "#A0A0A0" : "url(#paint0_linear_688_6203)"}
    />
    <defs>
      <linearGradient
        id="paint0_linear_688_6203"
        x1="1.49012e-07"
        y1="14"
        x2="9.79037"
        y2="-0.145095"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#100505" />
        <stop offset="1" stopColor="#511A10" />
      </linearGradient>
    </defs>
  </svg>
);
const UserIcon = ({ muted = false }: { muted?: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
  >
    <circle cx="7" cy="7" r="7" fill={muted ? "#A0A0A0" : "#220A0A"} />
    <path
      d="M7.12547 8.647C8.61358 8.64677 9.8078 7.44907 9.80075 5.95104C9.79382 4.49283 8.61804 3.28985 7.11513 3.29408C5.62643 3.29819 4.43456 4.50375 4.44795 5.99955C4.46099 7.46141 5.65239 8.64982 7.12547 8.647Z"
      fill={muted ? "#A0A0A0" : "#220A0A"}
      stroke="white"
      strokeMiterlimit="10"
    />
    <path
      d="M2 11.941C3.11288 10.4433 4.91161 9.47046 6.94118 9.47046C8.97074 9.47046 10.7695 10.4433 11.8824 11.941"
      stroke="white"
    />
  </svg>
);

export function Line({
  icon,
  children,
  muted = false,
}: {
  icon: "cal" | "clock" | "pin" | "user";
  children: React.ReactNode;
  muted?: boolean;
}) {
  const icons = {
    cal: CalIcon,
    clock: ClockIcon,
    pin: PinIcon,
    user: UserIcon,
  };
  const Icon = icons[icon];
  return (
    <div className="flex items-center gap-2 text-[12px] leading-[18px] text-garnet">
      <Icon muted={muted} />
      <span className="underline decoration-transparent hover:decoration-current transition">
        {children}
      </span>
    </div>
  );
}

export const SHIFT_CARD_BORDER_GOLD = "var(--pm-gradient-gold-2)";
export const SHIFT_CARD_BORDER_MUTED = "#545A6340";

export function ShiftCardItem({
  s,
  onClick,
  muted = false,
  hideUser = false,
  hideLocation = false,
  showVenue = false
}: {
  s: ShiftCard;
  onClick?: () => void;
  muted?: boolean;
  hideUser?: boolean;
  hideLocation?: boolean;
  showVenue?: boolean
}) {
  const role = useRole();
  const { t } = useTranslation("shifts");
  return (
    <article
      className={cn(
        "relative rounded-[4px] bg-fog p-4",
        onClick && "cursor-pointer",
      )}
      onClick={onClick}
    >
      {/* left accent */}
      <span
        className={cn(
          "absolute left-0 top-0 h-full w-[6px] rounded-[4px]",
          muted && `bg-[${SHIFT_CARD_BORDER_MUTED}]`,
          !muted && "[background-image:var(--pm-gradient-gold-2)]",
        )}
      />
      <div className="flex items-center gap-4 pl-3">
        <div className="flex-1">
          <div className="space-y-2">
            {/* Shift Title */}
            <div className="flex items-center gap-2 mb-1">
              <div
                className="text-[16px] font-bold text-garnet leading-tight truncate"
                title={s.name}
              >
                {s.name}
              </div>
              {s.pendingStatus ? (
                <span className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-warn-soft text-warn-deep">
                  {s.pendingStatus === "offered"
                    ? t("card.offered", "Offered to you")
                    : t("card.applied", "Pending approval")}
                </span>
              ) : null}
            </div>
            <div className="flex items-start justify-between gap-3 pr-2">
              <div className="text-[14px] font-semibold text-garnet">
                {s.roleLabel}{" "}
                <span className="font-normal text-garnet/70">
                  | ${s.ratePerHour}{t("card.ratePerHour")}
                  {s.total !== undefined
                    ? ` (${t("card.totalPrefix")}: $${s.total.toFixed(2)})`
                    : ""}
                </span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Line icon="cal" muted={muted}>
                {s.dateLabel}
              </Line>
              {s.days && s.days.length > 0 && (
                <div className="flex gap-1 flex-wrap">
                  {s.days.map((day: string) => {
                    const shortMap: Record<string, string> = {
                      MONDAY: t("days.mon"),
                      TUESDAY: t("days.tue"),
                      WEDNESDAY: t("days.wed"),
                      THURSDAY: t("days.thu"),
                      FRIDAY: t("days.fri"),
                      SATURDAY: t("days.sat"),
                      SUNDAY: t("days.sun"),
                    };
                    const short =
                      shortMap[day.toUpperCase()] || day.slice(0, 3);
                    return (
                      <span
                        key={day}
                        className="px-2 py-1 text-xs bg-garnet/10 text-garnet rounded-full"
                      >
                        {short}
                      </span>
                    );
                  })}
                </div>
              )}
              <Line icon="clock" muted={muted}>
                {s.timeLabel}
              </Line>
              {!hideLocation && (
                <Link
                  to={"/app/profile/employer/$employerId"}
                  params={{ employerId: String(s.businessId ?? "") }}
                >
                  <Line icon="pin" muted={muted}>
                    {s.businessName}
                  </Line>
                </Link>
              )}
              {showVenue&& (
                  <Line icon="pin" muted={muted}>
                    {s.venue}
                  </Line>
              )}
              {!hideUser &&
                (role === "worker" ? (
                  <Link
                    to="/app/profile/employer/$employerId"
                    params={{ employerId: String(s.businessId ?? "") }}
                  >
                    <Line icon="user" muted={muted}>
                      <span className="inline-flex items-center gap-2">
                        {s.businessName}
                        <StarRating
                          value={s.businessRating}
                          count={s.businessRatingCount}
                          size="sm"
                        />
                      </span>
                    </Line>
                  </Link>
                ) : s.workerId ? (
                  <Link
                    to="/app/profile/worker/$workerId"
                    params={{ workerId: String(s.workerId) }}
                  >
                    <Line icon="user" muted={muted}>
                      <span className="inline-flex items-center gap-2">
                        {s.workerName}
                        <StarRating
                          value={s.workerRating}
                          count={s.workerRatingCount}
                          size="sm"
                        />
                      </span>
                    </Line>
                  </Link>
                ) : (
                  <Line icon="user" muted={muted}>
                    <span className="inline-flex items-center gap-2">
                      {s.workerName}
                      <StarRating
                        value={s.workerRating}
                        count={s.workerRatingCount}
                        size="sm"
                      />
                    </span>
                  </Line>
                ))}
            </div>
          </div>
        </div>
        <RoleIcon
          value={s.roleValue}
          width={56}
          height={56}
          variant="gradient"
        />
      </div>
    </article>
  );
}

/**
 * Minimal card for dashboard upcoming shifts
 */
export function UpcomingShiftCardItem({ s }: { s: ShiftCard }) {
  const role = useRole();
  const navigate = useNavigate();
  const { t } = useTranslation("shifts");
  function onClick() {
    navigate({
      to: "/app/$role/dashboard/shift/$shiftId",
      params: { role, shiftId: s.id },
    });
  }
  return (
    <article
      className="relative rounded-[4px] bg-fog px-4 py-2 hover:bg-fog/80 transition cursor-pointer"
      onClick={onClick}
    >
      {/* left accent */}
      <span
        className={cn(
          "absolute left-0 top-0 h-full w-[6px] rounded-[4px]",
          "[background-image:var(--pm-gradient-gold-2)]",
        )}
      />
      <div className="flex items-center gap-4 pl-3">
        <div className="flex-1">
          <div className="text-[14px] font-bold text-garnet leading-tight mb-1 truncate">
            {s.roleLabel}{" "}
            <span className="text-[12px] font-normal text-garnet/70">
              | ${s.ratePerHour}{t("card.ratePerHour")} ({t("card.totalPrefix")}: ${s.total?.toFixed(2)})
            </span>
          </div>
          <div className="mt-2 space-y-1.5">
            <Line icon="cal">{s.dateLabel}</Line>
            <Line icon="clock">{s.timeLabel}</Line>
            {role === "worker" ? (
              <Line icon="pin">
                <Link
                  to="/app/profile/employer/$employerId"
                  params={{ employerId: String(s.businessId ?? "") }}
                  className="text-[12px]!"
                  onClick={(e) => e.stopPropagation()}
                >
                  {s.businessName}
                </Link>
              </Line>
            ) : s.workerId ? (
              <Line icon="user">
                <Link
                  to="/app/profile/worker/$workerId"
                  params={{ workerId: String(s.workerId) }}
                  className="text-[12px]!"
                  onClick={(e) => e.stopPropagation()}
                >
                  {s.workerName}
                </Link>
              </Line>
            ) : null}
          </div>
        </div>
        <RoleIcon
          value={s.roleValue}
          width={56}
          height={56}
          variant="gradient"
        />
      </div>
    </article>
  );
}
